import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import 'bootstrap/dist/css/bootstrap.min.css';
import ProgressBar from './ProgressBar';
import room1Image from '../../assets/Booking/DSC_0498.JPG';
import room2Image from '../../assets/Booking/DSC_0539.JPG';

const Search = () => {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useState({
        checkInDate: '',
        checkOutDate: '',
        adults: 1,
        children: 0,
        rooms: 1,
        childAges: [], // Array to store ages for each child
    });
    const [availableRooms, setAvailableRooms] = useState([
        { id: 1, name: 'Luxury Apartment Room', image: room1Image, pricePerNight: 30 }, // $30 per night
        { id: 2, name: 'Luxury Apartment Room with Villa', image: room2Image, pricePerNight: 50 }, // $50 per night
    ]);
    const [selectedRoom, setSelectedRoom] = useState(null);

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        if (name === 'children') {
            const newChildren = parseInt(value) || 0;
            // Reset child ages when children count changes
            setSearchParams(prev => ({
                ...prev,
                [name]: newChildren,
                childAges: Array(newChildren).fill(''), // Initialize empty ages for new children
            }));
        } else if (name.startsWith('childAge')) {
            const childIndex = parseInt(name.replace('childAge', '')) - 1;
            const newAges = [...searchParams.childAges];
            newAges[childIndex] = value;
            setSearchParams(prev => ({
                ...prev,
                childAges: newAges,
            }));
        } else {
            setSearchParams(prev => ({
                ...prev,
                [name]: name === 'adults' || name === 'rooms' ? parseInt(value) || 0 : value,
            }));
        }
    };

    const calculateTotalPrice = (room) => {
        if (!searchParams.checkInDate || !searchParams.checkOutDate) return 0;
        const checkIn = new Date(searchParams.checkInDate);
        const checkOut = new Date(searchParams.checkOutDate);
        const nights = Math.round((checkOut - checkIn) / (1000 * 60 * 60 * 24));
        return room.pricePerNight * nights * searchParams.rooms;
    };

    const handleSearch = () => {
        // Simulate searching for rooms (in a real app, this would hit an API)
        if (!searchParams.checkInDate || !searchParams.checkOutDate) {
            setAvailableRooms([]); // No rooms if dates aren't provided
        } else {
            // Keep the two rooms with their per-night prices
            setAvailableRooms([
                {
                    id: 1,
                    name: 'Luxury Apartment Room',
                    image: room1Image,
                    pricePerNight: 30
                },
                {
                    id: 2,
                    name: 'Luxury Apartment Room with Villa',
                    image: room2Image,
                    pricePerNight: 50
                },
            ]);
        }
        setSelectedRoom(null); // Reset selected room on new search
    };

    const handleBookNow = (room) => {
        if (!searchParams.checkInDate || !searchParams.checkOutDate) {
            alert('Please provide check-in and check-out dates before booking!');
            return;
        }

        const totalPrice = calculateTotalPrice(room);

        const bookingData = {
            room: { title: room.name, pricePerNight: room.pricePerNight },
            checkInDate: searchParams.checkInDate,
            checkOutDate: searchParams.checkOutDate,
            adults: searchParams.adults,
            children: searchParams.children,
            childAges: searchParams.childAges,
            rooms: searchParams.rooms,
            calculatedPrice: totalPrice,
        };

        navigate('/booking/booking', { state: bookingData });
    };

    return (
        <div className="container py-4">
            <ProgressBar currentStep={1} totalSteps={4} />
            <div className="row g-4">
                {/* Search Form (Left Column) */}
                <div className="col-md-4">
                    <div className="card p-3 shadow-sm">
                        <h4 className="mb-3">Search Rooms</h4>
                        <div className="mb-3">
                            <label htmlFor="checkInDate" className="form-label">Check-in Date</label>
                            <input
                                type="date"
                                id="checkInDate"
                                name="checkInDate"
                                className="form-control"
                                value={searchParams.checkInDate}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="checkOutDate" className="form-label">Check-out Date</label>
                            <input
                                type="date"
                                id="checkOutDate"
                                name="checkOutDate"
                                className="form-control"
                                value={searchParams.checkOutDate}
                                onChange={handleInputChange}
                            />
                        </div>
                        <div className="mb-3">
                            <label htmlFor="adults" className="form-label">Adults</label>
                            <select
                                id="adults"
                                name="adults"
                                className="form-select"
                                value={searchParams.adults}
                                onChange={handleInputChange}
                            >
                                {[1, 2, 3, 4].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>
                        <div className="mb-3">
                            <label className="form-label">Children</label>
                            <select
                                name="children"
                                className="form-select"
                                value={searchParams.children}
                                onChange={handleInputChange}
                            >
                                {[0, 1, 2, 3, 4].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                            {searchParams.children > 0 && (
                                <div className="mt-3">
                                    <h6>Child Ages</h6>
                                    {Array.from({ length: searchParams.children }, (_, index) => (
                                        <div key={index} className="mb-2">
                                            <label htmlFor={`childAge${index + 1}`} className="form-label">
                                                Child {index + 1} Age
                                            </label>
                                            <select
                                                id={`childAge${index + 1}`}
                                                name={`childAge${index + 1}`}
                                                className="form-select"
                                                value={searchParams.childAges[index] || ''}
                                                onChange={handleInputChange}
                                            >
                                                <option value="">Select Age</option>
                                                {Array.from({ length: 7 }, (_, i) => (
                                                    <option key={i} value={i}>{i}</option>
                                                ))}
                                            </select>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                        <div className="mb-3">
                            <label htmlFor="rooms" className="form-label">Rooms</label>
                            <select
                                id="rooms"
                                name="rooms"
                                className="form-select"
                                value={searchParams.rooms}
                                onChange={handleInputChange}
                            >
                                {[1, 2].map(num => (
                                    <option key={num} value={num}>{num}</option>
                                ))}
                            </select>
                        </div>
                        <button
                            className="btn btn-success w-100"
                            onClick={handleSearch}
                        >
                            Search
                        </button>
                    </div>
                </div>

                {/* Available Rooms (Right Column) */}
                <div className="col-md-8">
                    <h4 className="mb-3">Available Rooms</h4>
                    {availableRooms.length > 0 ? (
                        <div className="row row-cols-1 g-4">
                            {availableRooms.map(room => (
                                <div
                                    key={room.id}
                                    className={`col card h-100 ${selectedRoom?.id === room.id ? 'border-success shadow' : 'border-light'}`}
                                    onClick={() => setSelectedRoom(room)}
                                    style={{ cursor: 'pointer' }}
                                >
                                    <img src={room.image} alt={room.name} className="card-img-top" style={{ height: '400px', objectFit: 'cover' }} />
                                    <div className="card-body">
                                        <h5 className="card-title">{room.name}</h5>
                                        <p className="card-text">${room.pricePerNight} per night</p>
                                        <p className="card-text">Total: ${calculateTotalPrice(room)}</p>
                                        <button
                                            className="btn btn-success w-100"
                                            onClick={() => handleBookNow(room)}
                                        >
                                            Book Now
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <p className="text-muted">No rooms available. Please search for available dates.</p>
                    )}
                </div>

                {/* Important Notices Section */}
                <div className="col-12 mt-5">
                    <h4 class="mb-3 text-center">Important Notices</h4>
                    <div className="card p-3 shadow-sm">
                        <ul className="list-group list-group-flush">
                            <li className="list-group-item">
                                In an apartment, only 2 adults and 1 child are allowed.
                            </li>
                            <li className="list-group-item">
                                Children must be below 7 years old.
                            </li>
                            <li className="list-group-item">
                                Extra charges may apply for breakfast.
                            </li>
                            <li className="list-group-item">
                                Guests staying more than 10 days will receive free tuk-tuk service for city transport.
                            </li>
                            <li className="list-group-item">
                                Guests staying more than 10 days will receive a free boat ride in the Bentota River.
                            </li>
                            <li className="list-group-item">
                                Guests staying more than 30 days will incur a charge of Rs. 10,000 for airport transportation.
                            </li>
                        </ul>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Search;